import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export interface Tag {
  tag: string;
  value: string;
}

export interface Screen {
  id: string;
  name: string;
  group: string; // Group ID (empty string for ungrouped)
  orientation: string;
  tags: Tag[];
  status: string;
  createdAt: Date;
}

interface ScreenContextType {
  screens: Screen[];
  addScreen: (screen: Omit<Screen, "id" | "createdAt">) => void;
  updateScreen: (id: string, screen: Partial<Screen>) => void;
  deleteScreen: (id: string) => void;
  getScreenById: (id: string) => Screen | undefined;
  clearAllScreens: () => void; // Optional: to clear all screens
}

const ScreenContext = createContext<ScreenContextType | undefined>(undefined);

interface ScreenProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = "app_screens";

export const ScreenProvider: React.FC<ScreenProviderProps> = ({ children }) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load screens from localStorage on component mount
  useEffect(() => {
    try {
      const savedScreens = localStorage.getItem(STORAGE_KEY);
      if (savedScreens) {
        const parsedScreens = JSON.parse(savedScreens);
        // Convert createdAt strings back to Date objects
        const screensWithDates = parsedScreens.map((screen: any) => ({
          ...screen,
          createdAt: new Date(screen.createdAt),
        }));
        setScreens(screensWithDates);
        console.log(
          "ScreenContext: Loaded screens from localStorage:",
          screensWithDates
        );
      }
    } catch (error) {
      console.error(
        "ScreenContext: Error loading screens from localStorage:",
        error
      );
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save screens to localStorage whenever screens state changes
  useEffect(() => {
    if (isLoaded) {
      // Only save after initial load to avoid overwriting
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(screens));
        console.log("ScreenContext: Saved screens to localStorage:", screens);
      } catch (error) {
        console.error(
          "ScreenContext: Error saving screens to localStorage:",
          error
        );
      }
    }
  }, [screens, isLoaded]);

  const addScreen = (screenData: Omit<Screen, "id" | "createdAt">) => {
    const newScreen: Screen = {
      ...screenData,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      createdAt: new Date(),
    };

    console.log("ScreenContext: Adding new screen:", newScreen);
    setScreens((prev) => {
      const updated = [...prev, newScreen];
      console.log("ScreenContext: Updated screens after add:", updated);
      return updated;
    });
  };

  const updateScreen = (id: string, screenData: Partial<Screen>) => {
    console.log(
      "ScreenContext: Updating screen:",
      id,
      "with data:",
      screenData
    );
    setScreens((prev) => {
      const updated = prev.map((screen) =>
        screen.id === id ? { ...screen, ...screenData } : screen
      );
      console.log("ScreenContext: Updated screens after update:", updated);
      return updated;
    });
  };

  const deleteScreen = (id: string) => {
    console.log("ScreenContext: Deleting screen with ID:", id);
    setScreens((prev) => {
      const updated = prev.filter((screen) => screen.id !== id);
      console.log("ScreenContext: Updated screens after delete:", updated);
      return updated;
    });
  };

  const getScreenById = (id: string): Screen | undefined => {
    return screens.find((screen) => screen.id === id);
  };

  const clearAllScreens = () => {
    console.log("ScreenContext: Clearing all screens");
    setScreens([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("ScreenContext: Error clearing localStorage:", error);
    }
  };

  // Debug: Log when screens change
  useEffect(() => {
    console.log("ScreenContext: screens state changed:", screens);
  }, [screens]);

  const value: ScreenContextType = {
    screens,
    addScreen,
    updateScreen,
    deleteScreen,
    getScreenById,
    clearAllScreens,
  };

  return (
    <ScreenContext.Provider value={value}>{children}</ScreenContext.Provider>
  );
};

export const useScreens = (): ScreenContextType => {
  const context = useContext(ScreenContext);
  if (!context) {
    throw new Error("useScreens must be used within a ScreenProvider");
  }
  return context;
};
